import { useEffect, useRef } from "react";
import * as THREE from "three";
import { createNoise4D } from "simplex-noise";
import type { VoiceState } from "../voice/types";

interface VoiceSceneProps {
  sensitivity: "off" | "gentle" | "full";
  voiceState: VoiceState;
  getAudioLevel: () => number;
  visible: boolean;
}

const STATE_COLORS: Record<string, number> = {
  unloaded: 0xd4b48a,
  loading: 0x8b7bb8,
  ready: 0xd4b48a,
  speaking: 0x7eb88c,
  error: 0x8b5a5a,
};

// Phase 1: State-driven animation parameters
interface StateParams {
  rotSpeed: number;
  glowIntensity: number;
  breatheAmplitude: number;
  noiseSpeed: number;
}

const STATE_PARAMS: Record<string, StateParams> = {
  unloaded:  { rotSpeed: 0.001, glowIntensity: 0.06, breatheAmplitude: 0.6,  noiseSpeed: 0.15 },
  loading:   { rotSpeed: 0.005, glowIntensity: 0.12, breatheAmplitude: 1.0,  noiseSpeed: 0.35 },
  ready:     { rotSpeed: 0.002, glowIntensity: 0.08, breatheAmplitude: 0.8,  noiseSpeed: 0.2  },
  speaking:  { rotSpeed: 0.003, glowIntensity: 0.10, breatheAmplitude: 1.2,  noiseSpeed: 0.3  },
  error:     { rotSpeed: 0.001, glowIntensity: 0.05, breatheAmplitude: 0.4,  noiseSpeed: 0.1  },
};

export function VoiceScene({
  sensitivity,
  voiceState,
  getAudioLevel,
  visible,
}: VoiceSceneProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Refs so animation loop reads current values without scene recreation
  const sensitivityRef = useRef(sensitivity);
  const stateRef = useRef(voiceState);
  const getAudioLevelRef = useRef(getAudioLevel);

  useEffect(() => { sensitivityRef.current = sensitivity; }, [sensitivity]);
  useEffect(() => { stateRef.current = voiceState; }, [voiceState]);
  useEffect(() => { getAudioLevelRef.current = getAudioLevel; }, [getAudioLevel]);

  // Target color ref — animation loop lerps toward this each frame
  const targetColorRef = useRef(new THREE.Color(STATE_COLORS[voiceState] ?? STATE_COLORS.ready));

  useEffect(() => {
    targetColorRef.current = new THREE.Color(STATE_COLORS[voiceState] ?? STATE_COLORS.ready);
  }, [voiceState]);

  // Single scene, built once on mount
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (!canvasRef.current) return;
    const canvas: HTMLCanvasElement = canvasRef.current;

    const noise4D = createNoise4D();

    // ------------------------------------------------------------------ scene
    const scene = new THREE.Scene();
    const w = canvas.parentElement?.clientWidth || window.innerWidth;
    const h = canvas.parentElement?.clientHeight || window.innerHeight;

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
    camera.position.z = 5;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    } catch {
      console.warn("[VoiceScene] WebGL context creation failed — retrying without antialias");
      try {
        renderer = new THREE.WebGLRenderer({ canvas, alpha: true });
      } catch {
        console.error("[VoiceScene] WebGL unavailable");
        return;
      }
    }
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    // ---------------------------------------------------------- constellation
    // Initial color matches starting state
    const initialColor = new THREE.Color(STATE_COLORS[stateRef.current] ?? STATE_COLORS.ready);
    // currentColor is the lerped value that drives all material colors each frame
    const currentColor = initialColor.clone();

    const isMobile = window.innerWidth < 768;
    const pCount = isMobile ? 40 : 80;

    const pPos = new Float32Array(pCount * 3);
    const pBase = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount; i++) {
      pPos[i * 3]     = pBase[i * 3]     = (Math.random() - 0.5) * 10;
      pPos[i * 3 + 1] = pBase[i * 3 + 1] = (Math.random() - 0.5) * 8;
      // All particles behind the orb (z = -5 to -1), matching mockup exactly
      pPos[i * 3 + 2] = pBase[i * 3 + 2] = (Math.random() - 0.5) * 4 - 3;
    }

    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute("position", new THREE.BufferAttribute(pPos, 3));
    const particleMat = new THREE.PointsMaterial({
      color: currentColor.clone(),
      size: 0.04,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const particles = new THREE.Points(pGeo, particleMat);
    scene.add(particles);

    // Constellation lines (proximity-based connections on base positions)
    const connections: [number, number][] = [];
    const lineVerts: number[] = [];
    const MAX_D_SQ = 2.5 * 2.5;
    for (let i = 0; i < pCount; i++) {
      for (let j = i + 1; j < pCount; j++) {
        const dx = pBase[i * 3]     - pBase[j * 3];
        const dy = pBase[i * 3 + 1] - pBase[j * 3 + 1];
        const dz = pBase[i * 3 + 2] - pBase[j * 3 + 2];
        if (dx * dx + dy * dy + dz * dz < MAX_D_SQ) {
          connections.push([i, j]);
          lineVerts.push(
            pBase[i * 3], pBase[i * 3 + 1], pBase[i * 3 + 2],
            pBase[j * 3], pBase[j * 3 + 1], pBase[j * 3 + 2],
          );
        }
      }
    }

    let lines: THREE.LineSegments | null = null;
    let lineMat: THREE.LineBasicMaterial | null = null;
    let lGeo: THREE.BufferGeometry | null = null;
    if (lineVerts.length) {
      lGeo = new THREE.BufferGeometry();
      lGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(lineVerts), 3));
      lineMat = new THREE.LineBasicMaterial({
        color: currentColor.clone(),
        transparent: true,
        opacity: 0.06,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      lines = new THREE.LineSegments(lGeo, lineMat);
      scene.add(lines);
    }

    // -------------------------------------------------------------------- orb
    const orbGeo = new THREE.IcosahedronGeometry(1, 4);
    const originalPositions = new Float32Array(orbGeo.attributes.position.array);

    const orbMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        audioLevel: { value: 0 },
        color: { value: currentColor.clone() },
        glowColor: { value: currentColor.clone() },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform vec3 glowColor;
        uniform float audioLevel;
        varying vec3 vNormal;
        varying vec3 vPosition;
        void main() {
          vec3 viewDirection = normalize(cameraPosition - vPosition);
          float fresnel = 1.0 - dot(viewDirection, vNormal);
          fresnel = pow(fresnel, 2.0 + audioLevel * 2.0);
          vec3 finalColor = mix(color, glowColor, fresnel * 0.5);
          float brightness = 0.6 + audioLevel * 0.4;
          gl_FragColor = vec4(finalColor * brightness, 0.9);
        }
      `,
      transparent: true,
      wireframe: true,
    });

    const orb = new THREE.Mesh(orbGeo, orbMaterial);
    scene.add(orb);

    // Glow sphere — larger radius (1.3 vs old 1.15), sharper pow falloff (4.0 vs old 3.0),
    // softer base intensity (0.08 vs old 0.15) for the warm atmospheric halo look
    const glowGeo = new THREE.SphereGeometry(1.3, 32, 32);
    const glowMaterial = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: currentColor.clone() },
        audioLevel: { value: 0 },
        glowBase: { value: 0.08 },
      },
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float audioLevel;
        uniform float glowBase;
        varying vec3 vNormal;
        void main() {
          float intensity = pow(0.6 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 4.0);
          float glow = intensity * (glowBase + audioLevel * 0.25);
          gl_FragColor = vec4(color, glow);
        }
      `,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
    });

    const glowMesh = new THREE.Mesh(glowGeo, glowMaterial);
    scene.add(glowMesh);

    // ---------------------------------------------------- outer atmospheric glow
    const outerGlowGeo = new THREE.SphereGeometry(2.8, isMobile ? 16 : 24, isMobile ? 16 : 24);
    const outerGlowMat = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: currentColor.clone() },
        audioLevel: { value: 0 },
      },
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float audioLevel;
        varying vec3 vNormal;
        void main() {
          float intensity = pow(0.55 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 6.0);
          float glow = intensity * (0.03 + audioLevel * 0.08);
          gl_FragColor = vec4(color, glow);
        }
      `,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
    });

    const outerGlowMesh = new THREE.Mesh(outerGlowGeo, outerGlowMat);
    if (!isMobile) {
      scene.add(outerGlowMesh);
    }

    // --------------------------------------------------------- orbital particles
    const ORBITAL_COUNT = isMobile ? 0 : 2000;
    const orbitalGeo = new THREE.BufferGeometry();
    const oPositions = new Float32Array(ORBITAL_COUNT * 3);
    const oAngles = new Float32Array(ORBITAL_COUNT);
    const oRadii = new Float32Array(ORBITAL_COUNT);
    const oSpeeds = new Float32Array(ORBITAL_COUNT);
    const oSizes = new Float32Array(ORBITAL_COUNT);
    const oLifetimes = new Float32Array(ORBITAL_COUNT);

    for (let i = 0; i < ORBITAL_COUNT; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = (Math.random() - 0.5) * Math.PI * 0.8;
      const r = 1.3 + Math.random() * 1.7;

      oPositions[i * 3]     = r * Math.cos(phi) * Math.cos(theta);
      oPositions[i * 3 + 1] = r * Math.sin(phi);
      oPositions[i * 3 + 2] = r * Math.cos(phi) * Math.sin(theta);

      oAngles[i] = theta;
      oRadii[i] = r;
      oSpeeds[i] = 0.2 + Math.random() * 0.4;
      oSizes[i] = isMobile ? (0.3 + Math.random() * 0.5) : (0.3 + Math.random() * 0.7);
      oLifetimes[i] = Math.random();
    }

    orbitalGeo.setAttribute("position", new THREE.BufferAttribute(oPositions, 3));
    orbitalGeo.setAttribute("aAngle", new THREE.BufferAttribute(oAngles, 1));
    orbitalGeo.setAttribute("aRadius", new THREE.BufferAttribute(oRadii, 1));
    orbitalGeo.setAttribute("aSpeed", new THREE.BufferAttribute(oSpeeds, 1));
    orbitalGeo.setAttribute("aSize", new THREE.BufferAttribute(oSizes, 1));
    orbitalGeo.setAttribute("aLifetime", new THREE.BufferAttribute(oLifetimes, 1));

    const orbitalMat = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        audioLevel: { value: 0 },
        color: { value: currentColor.clone() },
        orbitalSpeedMul: { value: 1.0 },
        pointScale: { value: isMobile ? 20.0 : 30.0 },
      },
      vertexShader: `
        attribute float aAngle;
        attribute float aRadius;
        attribute float aSpeed;
        attribute float aSize;
        attribute float aLifetime;

        uniform float time;
        uniform float audioLevel;
        uniform float orbitalSpeedMul;
        uniform float pointScale;

        varying float vAlpha;

        void main() {
          float angle = aAngle + time * aSpeed * orbitalSpeedMul;
          float r = aRadius + audioLevel * 0.4;
          float vertOsc = sin(time * 0.7 + aLifetime * 6.2832) * 0.15;

          float basePhi = asin(clamp(position.y / aRadius, -1.0, 1.0));
          float phi = basePhi + vertOsc;

          vec3 pos;
          pos.x = r * cos(phi) * cos(angle);
          pos.y = r * sin(phi);
          pos.z = r * cos(phi) * sin(angle);

          float twinkle = sin(time * 2.0 + aLifetime * 12.566) * 0.3 + 0.7;
          vAlpha = twinkle * (0.06 + audioLevel * 0.15);

          vec4 mvPos = modelViewMatrix * vec4(pos, 1.0);
          gl_PointSize = aSize * (pointScale / -mvPos.z);
          gl_Position = projectionMatrix * mvPos;
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        varying float vAlpha;

        void main() {
          float d = length(gl_PointCoord - vec2(0.5));
          if (d > 0.5) discard;
          float alpha = smoothstep(0.5, 0.1, d) * vAlpha;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const orbitalParticles = new THREE.Points(orbitalGeo, orbitalMat);
    scene.add(orbitalParticles);

    // --------------------------------------------------------------- energy rings
    const ringConfigs = [
      { radius: 1.8, tube: 0.005, tiltX: 0.3,  tiltZ: 0.0,  speedDir:  1.0 },
      { radius: 2.1, tube: 0.004, tiltX: -0.2, tiltZ: 0.4,  speedDir: -0.7 },
      ...(isMobile ? [] : [{ radius: 2.4, tube: 0.003, tiltX: 0.1,  tiltZ: -0.3, speedDir:  0.5 }]),
    ];

    const ringSegments = isMobile ? 64 : 128;
    const rings: THREE.Mesh[] = [];
    const ringMaterials: THREE.ShaderMaterial[] = [];
    const ringGeometries: THREE.TorusGeometry[] = [];

    for (const cfg of ringConfigs) {
      const geo = new THREE.TorusGeometry(cfg.radius, cfg.tube, 16, ringSegments);
      const mat = new THREE.ShaderMaterial({
        uniforms: {
          color: { value: currentColor.clone() },
          alpha: { value: 0.15 },
        },
        vertexShader: `
          varying vec3 vNormal;
          void main() {
            vNormal = normalize(normalMatrix * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 color;
          uniform float alpha;
          varying vec3 vNormal;
          void main() {
            float edge = 1.0 - abs(dot(vNormal, vec3(0.0, 0.0, 1.0)));
            float a = edge * alpha;
            gl_FragColor = vec4(color, a);
          }
        `,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
      });

      const ring = new THREE.Mesh(geo, mat);
      ring.rotation.x = cfg.tiltX;
      ring.rotation.z = cfg.tiltZ;
      scene.add(ring);

      rings.push(ring);
      ringMaterials.push(mat);
      ringGeometries.push(geo);
    }

    // ---------------------------------------------------- mouse / interaction
    const mouse3D = new THREE.Vector3(0, 0, -100);
    let mouseActive = false;

    function onMouseMove(e: MouseEvent) {
      if (sensitivityRef.current === "off") return;
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = -(e.clientY / window.innerHeight) * 2 + 1;
      const v = new THREE.Vector3(nx, ny, 0.5).unproject(camera);
      const d = v.sub(camera.position).normalize();
      const dist = (-3 - camera.position.z) / d.z;
      mouse3D.copy(camera.position).add(d.multiplyScalar(dist));
      mouseActive = true;
    }
    document.addEventListener("mousemove", onMouseMove);

    // ------------------------------------------------------------- animation
    let time = Math.random() * 100;
    let lastFrame = performance.now();
    let audioLevel = 0;
    let breatheScale = 0;
    let animId: number;

    // Phase 1: mutable lerped params
    const currentParams: StateParams = { ...STATE_PARAMS.ready };

    function displace() {
      const positions = orb.geometry.attributes.position.array as Float32Array;
      const audioBoost = 1 + audioLevel * 0.5;

      for (let i = 0; i < positions.length; i += 3) {
        const ox = originalPositions[i];
        const oy = originalPositions[i + 1];
        const oz = originalPositions[i + 2];

        const length = Math.sqrt(ox * ox + oy * oy + oz * oz);
        const nx = ox / length;
        const ny = oy / length;
        const nz = oz / length;

        const noiseVal = noise4D(
          nx * 1.5 + time * 0.3,
          ny * 1.5 + time * 0.3,
          nz * 1.5,
          time * 0.2,
        );

        // Phase 1: polyrhythmic breathing using currentParams.breatheAmplitude
        const amp = currentParams.breatheAmplitude;
        const breathe = (
          Math.sin(time * 0.5)  * 0.015 +
          Math.sin(time * 0.83) * 0.008 +
          Math.sin(time * 1.37) * 0.004
        ) * amp;
        const displacement = noiseVal * 0.15 * audioBoost + breathe;

        positions[i]     = ox + nx * displacement;
        positions[i + 1] = oy + ny * displacement;
        positions[i + 2] = oz + nz * displacement;
      }
      orb.geometry.attributes.position.needsUpdate = true;
    }

    function animate() {
      animId = requestAnimationFrame(animate);
      const now = performance.now();
      const dt = Math.min((now - lastFrame) / 1000, 0.1);
      lastFrame = now;
      time += dt;

      // Audio level — synthetic pulse for loading state
      let targetLevel: number;
      if (stateRef.current === "loading") {
        targetLevel = 0.3 + Math.sin(time * 2) * 0.15;
      } else {
        targetLevel = getAudioLevelRef.current();
      }
      audioLevel += (targetLevel - audioLevel) * 0.1;
      if (stateRef.current === "ready" || stateRef.current === "unloaded") {
        audioLevel *= 0.95;
      }

      // Phase 1: lerp currentParams toward target state params
      const targetParams = STATE_PARAMS[stateRef.current] ?? STATE_PARAMS.ready;
      currentParams.rotSpeed += (targetParams.rotSpeed - currentParams.rotSpeed) * 0.03;
      currentParams.glowIntensity += (targetParams.glowIntensity - currentParams.glowIntensity) * 0.03;
      currentParams.breatheAmplitude += (targetParams.breatheAmplitude - currentParams.breatheAmplitude) * 0.03;
      currentParams.noiseSpeed += (targetParams.noiseSpeed - currentParams.noiseSpeed) * 0.03;

      // Phase 1: rotation wobble oscillators
      const breatheX = Math.sin(time * 0.41) * 0.006 + Math.sin(time * 0.97) * 0.003;
      const breatheY = Math.sin(time * 0.53) * 0.005 + Math.sin(time * 1.13) * 0.003;

      // Phase 1: breatheScale for glow sphere — shared via closure with displace()
      breatheScale =
        (Math.sin(time * 0.5) * 0.015 + Math.sin(time * 0.83) * 0.008 + Math.sin(time * 1.37) * 0.004) *
        currentParams.breatheAmplitude;

      // Smooth color lerp toward target state color — drives all materials each frame
      currentColor.lerp(targetColorRef.current, 0.05);
      orbMaterial.uniforms.color.value.copy(currentColor);
      orbMaterial.uniforms.glowColor.value.copy(currentColor);
      glowMaterial.uniforms.color.value.copy(currentColor);
      particleMat.color.lerp(targetColorRef.current, 0.02);
      if (lineMat) lineMat.color.lerp(targetColorRef.current, 0.02);

      // Orb uniforms
      orbMaterial.uniforms.time.value = time;
      orbMaterial.uniforms.audioLevel.value = audioLevel;
      glowMaterial.uniforms.audioLevel.value = audioLevel;
      // Phase 1: drive glowBase from currentParams
      glowMaterial.uniforms.glowBase.value = currentParams.glowIntensity;

      // Phase 1: rotation with wobble + z-axis drift
      displace();
      orb.rotation.y += currentParams.rotSpeed + breatheX * 0.1;
      orb.rotation.x += currentParams.rotSpeed * 0.5 + breatheY * 0.1;
      orb.rotation.z += Math.sin(time * 0.31) * 0.0003;
      glowMesh.rotation.y += currentParams.rotSpeed;
      glowMesh.rotation.x += currentParams.rotSpeed * 0.5;
      const glowScale = 1.0 + audioLevel * 0.15 + breatheScale * 0.3;
      glowMesh.scale.setScalar(glowScale);

      // Phase 4: outer atmospheric glow (desktop only)
      if (!isMobile) {
        outerGlowMat.uniforms.color.value.copy(currentColor);
        outerGlowMat.uniforms.audioLevel.value = audioLevel;
        const outerGlowScale = 1.0 + audioLevel * 0.08 + breatheScale * 0.15;
        outerGlowMesh.scale.setScalar(outerGlowScale);
      }

      // Phase 2: orbital particle uniforms
      orbitalMat.uniforms.time.value = time;
      orbitalMat.uniforms.audioLevel.value = audioLevel;
      orbitalMat.uniforms.color.value.copy(currentColor);
      orbitalMat.uniforms.orbitalSpeedMul.value = currentParams.rotSpeed / 0.002;

      // Phase 3: ring animation
      const ringSpeedMul = 1.0 + audioLevel * 0.5;
      for (let i = 0; i < rings.length; i++) {
        rings[i].rotation.y += ringConfigs[i].speedDir * 0.003 * ringSpeedMul * (currentParams.rotSpeed / 0.002);
        ringMaterials[i].uniforms.color.value.copy(currentColor);
        ringMaterials[i].uniforms.alpha.value = 0.12 + audioLevel * 0.2;
      }

      // Constellation particles
      const pushFactor =
        sensitivityRef.current === "full" ? 0.4 :
        sensitivityRef.current === "gentle" ? 0.2 : 0;

      const pp = particles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < pp.length; i += 3) {
        let px = pBase[i]     + Math.sin(time * 0.15 + i * 0.5) * 0.15;
        let py = pBase[i + 1] + Math.cos(time * 0.1  + i * 0.3) * 0.12;
        const pz = pBase[i + 2] + Math.sin(time * 0.08 + i * 0.7) * 0.05;

        if (mouseActive && pushFactor > 0) {
          const dx = px - mouse3D.x;
          const dy = py - mouse3D.y;
          const dSq = dx * dx + dy * dy;
          if (dSq < 6.25 && dSq > 0.01) {
            const dd = Math.sqrt(dSq);
            const f = 1 - dd / 2.5;
            const push = f * f * pushFactor;
            px += (dx / dd) * push;
            py += (dy / dd) * push;
          }
        }

        pp[i]     = px;
        pp[i + 1] = py;
        pp[i + 2] = pz;
      }
      particles.geometry.attributes.position.needsUpdate = true;

      // Constellation lines follow particles
      if (lines && lGeo) {
        const lp = lines.geometry.attributes.position.array as Float32Array;
        let idx = 0;
        for (const [ci, cj] of connections) {
          lp[idx]     = pp[ci * 3];
          lp[idx + 1] = pp[ci * 3 + 1];
          lp[idx + 2] = pp[ci * 3 + 2];
          lp[idx + 3] = pp[cj * 3];
          lp[idx + 4] = pp[cj * 3 + 1];
          lp[idx + 5] = pp[cj * 3 + 2];
          idx += 6;
        }
        lines.geometry.attributes.position.needsUpdate = true;
      }

      renderer.render(scene, camera);
    }

    animate();

    // ----------------------------------------------------------------- resize
    function onResize() {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rw = parent.clientWidth;
      const rh = parent.clientHeight;
      if (rw === 0 || rh === 0) return;
      camera.aspect = rw / rh;
      camera.updateProjectionMatrix();
      renderer.setSize(rw, rh);
    }
    window.addEventListener("resize", onResize);

    // ----------------------------------------------------------------- cleanup
    return () => {
      cancelAnimationFrame(animId);
      document.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);

      pGeo.dispose();
      particleMat.dispose();
      if (lGeo) lGeo.dispose();
      if (lineMat) lineMat.dispose();
      orbGeo.dispose();
      orbMaterial.dispose();
      glowGeo.dispose();
      glowMaterial.dispose();
      outerGlowGeo.dispose();
      outerGlowMat.dispose();
      orbitalGeo.dispose();
      orbitalMat.dispose();
      for (let i = 0; i < rings.length; i++) {
        ringGeometries[i].dispose();
        ringMaterials[i].dispose();
      }

      renderer.dispose();
    };
  }, []); // empty — scene builds once, refs carry all live values

  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0 }}>
      <canvas
        ref={canvasRef}
        style={{
          pointerEvents: "none",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.3s ease",
          display: "block",
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
}
